import { create } from 'zustand';

import { onClientHydrated } from '#/rendering/state/renderingState';
import {
  getLocalStorageJsonItem,
  removeLocalStorageItem,
  setLocalStorageJsonItem,
} from '#/utils/localStorage';
import { log } from '#/utils/logging';

type Args<TData> = {
  key: string;
  getInitializationValue?: (localStorageDta: TData | null) => TData;
};

export function createLocalStorageStore<TData>({
  key,
  getInitializationValue,
}: Args<TData>) {
  const store = create<{ value: TData | null }>(() => ({ value: null }));

  const getValue = () => store.getState().value;

  const useStore = store; // The store can be used as a hook
  const useValue = () => useStore((state) => state.value);

  onClientHydrated(() => {
    const dataFromLocalStorage = getLocalStorageJsonItem<TData>(key);

    const transformedData = getInitializationValue
      ? getInitializationValue(dataFromLocalStorage)
      : dataFromLocalStorage;

    store.setState({ value: transformedData });
  });

  const setValue = (newValue: TData | null) => {
    if (typeof newValue === 'undefined' || newValue === null) {
      removeLocalStorageItem(key);
      store.setState({ value: null });
      return null;
    }

    try {
      setLocalStorageJsonItem(key, newValue);
      store.setState({ value: newValue });
    } catch (e) {
      log.error(e);
    }
  };

  return { getValue, useValue, setValue };
}
